import api from "@/api";
import moment from "moment-timezone";
import {
  formatPhoneNumber,
  getEstimatedPrice,
  packageProductsForOrderDetails,
} from "@/util.js";
import { loadStripe } from "@stripe/stripe-js";
import router from "@/router";

const LIMIT = 30;
let page = 1;
let end = false;
let stripe = null;

(async function () {
  stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY);
})();

const formatMomentDate = (serviceNowMoment) => {
  return serviceNowMoment.format("YYYY-MM-DD");
};

const formatMomentTime = (serviceNowMoment) => {
  return serviceNowMoment.format("HH:mm");
};
const formatPriceAndWeight = (replaceDetails) => {
  if (!replaceDetails) return;
  replaceDetails.cents = replaceDetails.priceWithModifiers / replaceDetails.qty;
  replaceDetails.qty = replaceDetails.byweight
    ? replaceDetails.qty / 100
    : replaceDetails.qty;
};
const getMinimalistOrder = ({
  bringg_delivery_estimate_local,
  cash_discount_fee,
  chosen_time,
  created_at,
  delivery_address,
  delivery_amount,
  delivery_status,
  delivery_method_id,
  first_order_discount,
  is_authorized,
  order_id,
  status,
  store_delivery_method_id,
  totals,
}) => {
  return {
    bringg_delivery_estimate_local,
    cash_discount_fee,
    chosen_time,
    created_at,
    delivery_address,
    delivery_amount,
    delivery_status,
    delivery_method_id,
    first_order_discount,
    is_authorized,
    order_id,
    status,
    store_delivery_method_id,
    totals,
  };
};

const getMomentNow = (timeZone) => {
  return moment().tz(timeZone);
};

const getServiceNowDateAndTime = (timeZone, waitTime) => {
  const serviceNowMoment = getServiceNowMoment(timeZone, waitTime);
  return {
    date: formatMomentDate(serviceNowMoment),
    time: formatMomentTime(serviceNowMoment),
  };
};

const getServiceNowMoment = (timeZone, waitTime) => {
  return getMomentNow(timeZone).add(waitTime, "minutes");
};

const getDefaultAddressState = () => {
  return {
    address: {
      address: "",
      apartment: "",
      city: "",
      full_address: "",
      state: "",
      zip: "",
    },
    lastFormattedAddress: "",
  };
};

const getDefaultOrderState = () => {
  return {
    alcoholOrTobaccoProduct: false,
    comment: "",
    customerName: null,
    driverTip: 0,
    id: "",
    missingId: 1, // CALL_ME
    noCC: null,
    phone: null,
    selectedDate: {},
    selectedTime: {},
    service: null,
    serviceNow: false,
  };
};

const getDefaultPaymentState = () => {
  return {
    clientSecret: null,
    card: null,
    cards: [],
    selectedCard: {
      id: "",
    },
  };
};

const getDefaultSelectedCardState = () => {
  return {
    id: "",
  };
};

const mergePickStatus = (products, pickStatus) => {
  if (!pickStatus) return products;
  if (Array.isArray(products)) {
    products.forEach((product) => {
      product.pickStatus = pickStatus.find(
        (item) => item.item_id == product.item_id,
      );
      formatPriceAndWeight(product.pickStatus?.replaceDetails);
    });
  } else {
    Object.keys(products).forEach((key) => {
      products[key].pickStatus = pickStatus.find(
        (item) => item.item_id == products[key].item_id,
      );
      formatPriceAndWeight(products[key].pickStatus?.replaceDetails);
    });
  }
  return products;
};

const removeOrdersWithoutDetails = (order) => order.details;

const setPricesForOlderOrders = (products) => {
  return products.map((product) => {
    const cents = product.final_price / product.qty;
    return {
      ...product,
      cents,
      priceWithModifiers: getEstimatedPrice({ ...product, cents }),
    };
  });
};

const setIsExpiredOnCard = (card, tz) => {
  var { exp_year, exp_month } = card.card;
  return { ...card, isExpired: isExpiredCard({ exp_year, exp_month }, tz) };
};

const isExpiredCard = (expDate, tz) => {
  const now = moment().tz(tz);
  const { month, year } = expDate;
  const targetDate = moment({ year, month: month - 1 }).tz(tz);
  targetDate.endOf("month");
  return targetDate.isAfter(now, "day");
};

const initOldOrderTotals = ({ ccFee, subtotal, total }) => ({
  ccFee,
  fee: 0,
  saving: 0,
  subtotal,
  tax: 0,
  total,
});

const initRecentOrderTotals = (authorizedCreditCard, totals) => {
  const ccFee = totals.ccFee || 0;
  return {
    ...totals,
    total: authorizedCreditCard ? totals.total : totals.total - ccFee,
  };
};

const packageOlderOrder = (order, products, total, pickStatus) => {
  products = mergePickStatus(products, pickStatus);
  const ccFee =
    order.cash_discount_fee && order.is_authorized
      ? order.cash_discount_fee
      : 0;

  const deliveryAmount = order.delivery_amount || 0;
  const firstOrderDiscount = order.first_order_discount || 0;

  const totalsObject = {
    ccFee,
    subtotal: total - ccFee - deliveryAmount + firstOrderDiscount,
    total,
  };

  return {
    _products: initProductsForReorder(products),
    created_at: order.created_at,
    delivery_address: order.delivery_address || null,
    delivery_amount: deliveryAmount,
    delivery_method_id: order.delivery_method_id || null,
    first_order_discount: firstOrderDiscount,
    order_id: order.order_id,
    products: setPricesForOlderOrders(products),
    totals: initOldOrderTotals(totalsObject),
  };
};

const packageRecentOrder = (
  order,
  productsForReOrder,
  productsPreserved,
  pickStatus,
  isPicked,
) => {
  const isBringgOrder = order.delivery_method_id === 3;
  const isMyteamOrder = order.delivery_method_id === 2;
  const isPickupOrder = !order.delivery_method_id;

  const bringgOrderDetails = {};
  const myteamOrderDetails = {};
  const pickupOrder = {};

  if (isBringgOrder) {
    bringgOrderDetails.bringg_delivery_estimate_local =
      order.bringg_delivery_estimate_local;
    bringgOrderDetails.delivery_status = order.delivery_status;
  }

  if (isMyteamOrder) {
    myteamOrderDetails.pickupTime = moment(order.chosen_time, "HH:mm").format(
      "h:mm A",
    );
  }

  if (isPickupOrder) {
    pickupOrder.pickupTime = moment(order.chosen_time, "HH:mm").format(
      "h:mm A",
    );
  }

  return {
    _products: initProductsForReorder(productsForReOrder),
    bringgOrderDetails,
    created_at: order.created_at,
    delivery_address: order.delivery_address || null,
    delivery_amount: order.delivery_amount || 0, // init because used in calc and value null if not delivery order
    delivery_method_id: order.delivery_method_id || null,
    first_order_discount: order.first_order_discount || 0,
    isPicked,
    items: productsPreserved,
    myteamOrderDetails,
    order_id: order.order_id,
    pickupOrder,
    products: mergePickStatus(
      packageProductsForOrderDetails(productsPreserved),
      pickStatus,
    ),
    status: order.status,
    totals: initRecentOrderTotals(order.is_authorized, order.totals),
  };
};

const removedLastCard = (cards) => {
  return !cards.length;
};

const removedSelectedCard = (selectedCardToken, tokenToRemove) => {
  return selectedCardToken === tokenToRemove;
};

const state = () => ({
  awaitingPaymentData: false,
  data: null,
  dialog: {
    backToCart: false,
    minimumOrderAmount: false,
    orderError: { error: false, message: "" },
    orderReceived: false,
    payment: false,
    cards: false,
  },
  loaded: false,
  order: {
    ...getDefaultAddressState(),
    ...getDefaultOrderState(),
    readyToPay: false,
  },
  payNow: getDefaultPaymentState(),
  rerouteOrderId: "",
  processingSavedCC: false,
});

const actions = {
  async addOrder({ commit, dispatch }, { orderId, setOrderForConfirmation }) {
    commit("ADD_ORDER_ID", orderId);

    const data = await api({
      params: { filter: orderId },
      url: "/123/orders/store_orders",
    });

    if (data.err) return dispatch("error/showError", data.err, { root: true });
    if (setOrderForConfirmation) {
      return dispatch("orders/addOrderAndSetForConfirmation", data.info[0], {
        root: true,
      });
    }

    commit("ADD_ORDER", data.info[0]);
  },

  async addOrderAndSetForConfirmation({ commit }, order) {
    commit("SET_ORDER_FOR_CONFIRMATION", order);
    commit("ADD_ORDER", order);
  },

  error({ commit }, err) {
    commit("AWAITING_PAYMENT_DATA", false);
    commit("RESET_ORDER_STATE");
    commit("OPEN_ORDER_ERROR_DIALOG", err);
    // dispatch("error/showError", err, { root: true });
  },

  async fetch({ commit, dispatch }) {
    if (end) {
      return;
    }

    const data = await api({
      params: { pick_status: true, limit: LIMIT, page, sortby: "order_id" },
      url: "/123/orders/store_orders",
    });

    const { err, info } = data;

    if (err) return dispatch("error/showError", err, { root: true });
    if (!info.length) return commit("INITIALIZE_EMPTY_ORDERS");

    const formattedOrders = info
      .filter(removeOrdersWithoutDetails)
      .map((order) => {
        const productsForReOrder = order.details.products;
        const productsPreserved = order.items;
        const pickStatus = order.pick_status?.order ?? [];
        const minimalOrder = getMinimalistOrder(order);
        const isPicked = order.pick_status;

        if (order.bringg_delivery_estimate_local) {
          const deliveryEta = moment(
            order.bringg_delivery_estimate_local,
          ).format("h:mm a");
          dispatch("orderStatus/setOrderEta", deliveryEta, { root: true });
        }

        if (productsPreserved) {
          return packageRecentOrder(
            minimalOrder,
            productsForReOrder,
            productsPreserved,
            pickStatus,
            isPicked,
          );
        }

        return packageOlderOrder(minimalOrder, productsForReOrder, order.total);
      });

    commit("UPDATE_ORDERS", formattedOrders);
  },
  async generatePaymentIntent(
    { commit, dispatch, rootState },
    { orderId, amount },
  ) {
    const tz = rootState.store.info.tz;
    commit("ADD_ORDER_ID", orderId);
    const data = await api({
      data: { amount },
      method: "POST",
      url: "/123/payments/token",
    });

    if (data.err) {
      return dispatch("orders/paymentError", data.err, { root: true });
    }

    if (data.info.customer_cards.length) {
      commit("SET_CREDIT_CARD_DIALOG", { cards: data.info.customer_cards, tz });
      return dispatch(
        "orders/initializeStripePaymentCustomerCards",
        data.info.client_secret,
        { root: true },
      );
    }

    dispatch("orders/initializeStripePayment", data.info.client_secret, {
      root: true,
    });
  },

  // async getOrderForConfirmation({ commit, dispatch }, orderId) {
  //   const data = await api({
  //     params: { filter: orderId },
  //     url: "/123/orders/store_orders",
  //   });
  //   data.err
  //     ? dispatch("error/showError", data.err, { root: true })
  //     : commit("SET_ORDER_FOR_CONFIRMATION", { order: data.info[0] });
  // },
  initializeStripePayment({ commit }, clientSecret) {
    commit("AWAITING_PAYMENT_DATA", false);
    commit("SET_CLIENT_SECRET", clientSecret);
    commit("OPEN_PAYMENT_DIALOG");
  },
  initializeStripePaymentCustomerCards({ commit }, clientSecret) {
    commit("AWAITING_PAYMENT_DATA", false);
    commit("SET_CLIENT_SECRET", clientSecret);
    commit("OPEN_CREDIT_CARD_DIALOG");
  },
  async payInStoreOrder({ commit, dispatch, rootState, state }) {
    commit("AWAITING_PAYMENT_DATA", true);

    const { store, user } = rootState;

    //  Add D&T here
    state.order.serviceNow &&
      commit("UPDATE_SELECTED_DATE_AND_TIME", {
        timeZone: store.info.tz,
        waitTime: store.info.waitTime,
      });

    const {
      comment,
      customerName,
      missingId,
      noCC,
      phone,
      selectedDate,
      selectedTime,
    } = state.order;

    const name = customerName || user.info.name;

    const order = {
      chosen_date: selectedDate.iso,
      chosen_time: selectedTime.time,
      comment,
      customer_name: name,
      missing_id: missingId,
      no_cc: noCC,
      phone: phone
        ? phone.replace(/\D/g, "")
        : user.info.phone.replace(/\D/g, ""),
    };
    const data = await api({
      data: order,
      method: "POST",
      url: "/123/orders/confirm",
    });
    data.err
      ? dispatch("orders/error", data.err, { root: true })
      : dispatch("orders/payInStoreSuccess", data.info.scalar, {
          root: true,
        });
  },

  payNowSuccess({ commit }) {
    commit("cart/CLEAR", null, { root: true });
    api({
      method: "POST",
      url: "/123/payments/authorize",
    });
  },

  paymentError({ commit }, err) {
    commit("AWAITING_PAYMENT_DATA", false);
    commit("RESET_ORDER_STATE_ON_PAYMENT_ERROR");
    commit("OPEN_ORDER_ERROR_DIALOG", err);
  },

  async payNowOrder({ commit, dispatch, rootState, state }, amount) {
    commit("AWAITING_PAYMENT_DATA", true);

    const { store, user, cart } = rootState;
    const store_delivery_method_id = store.info.store_delivery_method_id;

    const bringgDate = cart.bringgDeliveryDate || null;
    const { pickup_time_mins, tz } = store.info;
    const bringgTime =
      cart.bringgDeliveryETA &&
      getServiceNowMoment(tz, pickup_time_mins).format("HH:mm");

    if (state.order.serviceNow) {
      commit("UPDATE_SELECTED_DATE_AND_TIME", {
        timeZone: store.info.tz,
        waitTime: store.info.waitTime,
      });
    }

    const {
      address,
      comment,
      customerName,
      driverTip,
      missingId,
      phone,
      selectedDate,
      selectedTime,
      service,
    } = state.order;

    const name = customerName || user.info.name;
    const order = {
      chosen_date:
        bringgDate && service === "DELIVERY" ? bringgDate : selectedDate.iso,
      chosen_time:
        bringgTime && service === "DELIVERY" ? bringgTime : selectedTime.time,
      comment,
      customer_name: name,
      driver_tip: driverTip,
      missing_id: missingId,
      phone: phone
        ? phone.replace(/\D/g, "")
        : user.info.phone.replace(/\D/g, ""),
      ...(service === "DELIVERY" && { store_delivery_method_id }),
      ...(service === "DELIVERY" && {
        delivery_address: { ...address, customer_name: name },
      }),
    };
    const data = await api({
      data: order,
      method: "POST",
      url: "/123/orders/confirm",
    });

    if (data.err) return dispatch("orders/error", data.err, { root: true });

    return dispatch(
      "orders/generatePaymentIntent",
      { orderId: data.info.scalar, amount },
      { root: true },
    );
  },

  async payInStoreSuccess({ commit }, orderId) {
    commit("AWAITING_PAYMENT_DATA", false);
    commit("cart/CLEAR", null, { root: true });

    const params = { orderId: orderId };
    return router.push({ path: "/", query: params });

    // commit("OPEN_ORDER_RECEIVED_DIALOG");
  },

  async payWithSavedCC({ commit, dispatch, state }) {
    commit("AWAITING_PAYMENT_DATA", true);
    const data = await stripe.confirmCardPayment(state.payNow.clientSecret, {
      payment_method: state.payNow.selectedCard.id,
    });
    commit("AWAITING_PAYMENT_DATA", false);

    if (data.error) {
      dispatch(
        "orders/paymentError",
        data.error.message || "Unexpected error. Card cannot be charged.",
        { root: true },
      );
      return;
    }
    await dispatch("payNowSuccess");
  },

  async removeCreditCard({ commit, dispatch, state }, token) {
    commit("REMOVE_CREDIT_CARD", token);
    if (
      // if removed credit card was selected card, or was the last card?
      removedLastCard(state.payNow.cards) ||
      removedSelectedCard(state.payNow.selectedCard.id, token)
    ) {
      commit("UPDATE_SELECTED_CARD", getDefaultSelectedCardState());
    }
    const data = await api({
      method: "DELETE",
      url: `/123/payments/card/${token}`,
    });

    data.err && dispatch("error/showError", data.err, { root: true });

    commit("OPEN_CREDIT_CARD_DIALOG");
  },

  setReadyToPay({ commit }, bool) {
    commit("UPDATE_READY_TO_PAY", bool);
  },
};

const getters = {
  awaiting({ awaitingPaymentData }) {
    return awaitingPaymentData;
  },
  loaded({ data }) {
    return !!data;
  },
  serviceIsDelivery(state) {
    return state.order.service;
  },
};

const initOrderProductForReorder = (product) => ({
  ...product,
  cents: product.upc_cents,
  item_id: null,
  qty: 1, // 1 like server returns for all products (not true, qty can be anything, but mostly 1!!)
});

const prioritizeProductsWithImage = (firstProduct, secondProduct) => {
  if (firstProduct.image === secondProduct.image) {
    return 0;
  } else if (firstProduct.image === null) {
    return 1;
  } else if (secondProduct.image === null) {
    return -1;
  }
};

const uniqueUpc = (product, index, products) =>
  index === products.findIndex((p) => p.upc === product.upc);

const initProductsForReorder = (products) => {
  return products
    .filter(uniqueUpc)
    .map(initOrderProductForReorder)
    .sort(prioritizeProductsWithImage);
};

const mutations = {
  ADD_ORDER(state, order) {
    const productsForReOrder = order.details.products;
    const productsPreserved = order.items;
    const minimalOrder = getMinimalistOrder(order);
    const packagedOrder = packageRecentOrder(
      minimalOrder,
      productsForReOrder,
      productsPreserved,
    );

    const orders = state.data
      ? state.data.filter((obj) => obj.order_id !== order.order_id)
      : [];

    // state.data = unshift
    //   ? [packagedOrder, ...orders]
    //   : [...orders, packagedOrder];
    state.data = [packagedOrder, ...orders];
  },
  ADD_ORDER_ID(state, orderId) {
    state.order.id = orderId;
  },
  AWAITING_PAYMENT_DATA(state, bool) {
    state.awaitingPaymentData = bool;
  },
  CLEAR(state) {
    state.data = [];
    Object.assign(state.order, getDefaultAddressState());
    Object.assign(state.order, getDefaultOrderState());
    Object.assign(state.payNow, getDefaultPaymentState());
  },
  CLOSE_BACK_TO_CART_DIALOG(state) {
    state.dialog.backToCart = false;
  },
  CLOSE_CREDIT_CARD_DIALOG(state) {
    state.dialog.cards = false;
  },
  CLOSE_MINIMUM_ORDER_AMOUNT_DIALOG(state) {
    state.dialog.minimumOrderAmount = false;
  },
  CLOSE_ORDER_ERROR_DIALOG(state) {
    state.dialog.orderError.error = false;
    state.dialog.orderError.message = "";
  },
  CLOSE_ORDER_RECEIVED_DIALOG(state) {
    state.dialog.orderReceived = false;
    Object.assign(state.order, getDefaultOrderState());
    Object.assign(state.payNow, getDefaultPaymentState());
  },
  CLOSE_PAYMENT_DIALOG(state) {
    state.dialog.payment = false;
    // Object.assign(state.order, getDefaultOrderState());
    Object.assign(state.payNow, getDefaultPaymentState());
  },
  INITIALIZE_EMPTY_ORDERS(state) {
    state.data = [];
  },
  PROCESSING_SAVED_CARD(state, processing) {
    state.processingSavedCC = processing;
  },
  OPEN_BACK_TO_CART_DIALOG(state) {
    state.dialog.backToCart = true;
  },
  OPEN_CREDIT_CARD_DIALOG(state) {
    state.dialog.cards = true;
  },
  OPEN_MINIMUM_ORDER_AMOUNT_DIALOG(state) {
    state.dialog.minimumOrderAmount = true;
  },
  OPEN_ORDER_ERROR_DIALOG(state, message) {
    state.dialog.orderError.error = true;
    state.dialog.orderError.message = message;
  },
  OPEN_ORDER_RECEIVED_DIALOG(state) {
    state.dialog.orderReceived = true;
  },
  OPEN_PAYMENT_DIALOG(state) {
    state.dialog.payment = true;
  },
  REMOVE_CREDIT_CARD(state, token) {
    state.payNow.cards = state.payNow.cards.filter(({ id }) => id !== token);
  },
  RESET_ORDER_STATE(state) {
    Object.assign(state.order, getDefaultOrderState());
    Object.assign(state.payNow, getDefaultPaymentState());
  },
  RESET_ORDER_STATE_ON_PAYMENT_ERROR(state) {
    Object.assign(state.payNow, getDefaultPaymentState());
  },
  ROUTE_TO_ORDER_HISTORY(state, orderId) {
    state.rerouteOrderId = orderId;
  },
  SET_ADDRESS(state, address) {
    state.order.address = { ...state.order.address, ...address };
  },
  SET_APARTMENT(state, apartment) {
    state.order.address.apartment = apartment;
  },
  SET_CLIENT_SECRET(state, clientSecret) {
    state.awaitingPaymentData = false;
    state.payNow.clientSecret = clientSecret;
  },
  SET_CREDIT_CARD_DIALOG(state, { cards, tz }) {
    state.payNow.cards = cards.map((card) => setIsExpiredOnCard(card, tz));
    //state.payNow.selectedCard = setDefaultSelectedCard(state.payNow.cards);
  },
  SET_FULL_ADDRESS(state, fullAddress) {
    state.order.address.full_address = fullAddress;
  },
  SET_ORDER_FOR_CONFIRMATION(state, order) {
    state.order.alcoholOrTobaccoProduct = order.details.products.some(
      (_) => _.is_alcohol || _.is_tobacco,
    );
    state.order.noCC = false;
    state.order.selectedDate.iso = order.chosen_date;
    state.order.selectedTime.time = order.chosen_time;
    state.order.service = order.delivery_method_id ? "Delivery" : "Pickup";
  },

  UPDATE(state, { name, val }) {
    state.order[name] = val;
  },

  UPDATE_ADDRESS(state, address) {
    state.order.address = address;
  },

  UPDATE_ORDERS(state, formattedOrders) {
    state.data = formattedOrders;
  },

  UPDATE_ORDERS_STATUS(state, { id, status }) {
    state.data = state.data?.map((order) => {
      if (order.order_id.toString() === id) {
        return { ...order, status: status };
      }
      return order;
    });
  },

  UPDATE_PHONE(state, val) {
    state.order.phone = formatPhoneNumber(val);
  },

  UPDATE_SELECTED_CARD(state, card) {
    state.payNow.selectedCard = card;
  },

  UPDATE_SELECTED_DATE_AND_TIME(state, { timeZone, waitTime }) {
    const serviceNow = getServiceNowDateAndTime(timeZone, waitTime);
    state.order.selectedDate = { iso: serviceNow.date };
    state.order.selectedTime = { time: serviceNow.time };
  },

  UPDATE_SERVICE_TYPE(state, service) {
    state.order.service = service;
  },

  UPDATE_READY_TO_PAY(state, bool) {
    state.order.readyToPay = bool;
  },
};

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
};
